/**
 * This class was vibecoded by Claude. It uses the built in audio context
 * oscillation library to create custom success and fail sounds
 * ref: https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/createOscillator
 */
export class AudioNotificationService {
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;

  constructor() {
    this.initializeAudioContext();
  }

  private initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('AudioContext not supported:', error);
      this.audioContext = null;
    }
  }

  private async resumeAudioContext() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.warn('Failed to resume audio context:', error);
      }
    }
  }

  /**
   * Play an error sound notification
   */
  async playErrorSound() {
    if (!this.isEnabled || !this.audioContext) {
      return;
    }

    try {
      await this.resumeAudioContext();
      
      if (this.audioContext.state !== 'running') {
        return;
      }

      // Create a harsh error tone - two quick beeps
      const oscillator1 = this.audioContext.createOscillator();
      const oscillator2 = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      // Connect nodes
      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure frequencies for a harsh error sound
      oscillator1.frequency.value = 600; // High frequency
      oscillator2.frequency.value = 400; // Lower frequency for discord

      // Configure volume envelope for quick beep
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.2);

      // Start and stop oscillators for first beep
      oscillator1.start(this.audioContext.currentTime);
      oscillator1.stop(this.audioContext.currentTime + 0.2);
      oscillator2.start(this.audioContext.currentTime);
      oscillator2.stop(this.audioContext.currentTime + 0.2);

      // Second beep after short pause
      setTimeout(() => {
        if (!this.audioContext || this.audioContext.state !== 'running') return;

        const oscillator3 = this.audioContext.createOscillator();
        const oscillator4 = this.audioContext.createOscillator();
        const gainNode2 = this.audioContext.createGain();

        oscillator3.connect(gainNode2);
        oscillator4.connect(gainNode2);
        gainNode2.connect(this.audioContext.destination);

        oscillator3.frequency.value = 800;
        oscillator4.frequency.value = 400;

        gainNode2.gain.setValueAtTime(0, this.audioContext.currentTime);
        gainNode2.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.05);
        gainNode2.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.2);

        oscillator3.start(this.audioContext.currentTime);
        oscillator3.stop(this.audioContext.currentTime + 0.2);
        oscillator4.start(this.audioContext.currentTime);
        oscillator4.stop(this.audioContext.currentTime + 0.2);
      }, 300);

    } catch (error) {
      console.warn('Failed to play error sound:', error);
    }
  }

  /**
   * Play a success sound notification (optional for completeness)
   */
  async playSuccessSound() {
    if (!this.isEnabled || !this.audioContext) {
      return;
    }

    try {
      await this.resumeAudioContext();
      
      if (this.audioContext.state !== 'running') {
        return;
      }

      // Create a pleasant success tone
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure for a gentle success sound
      oscillator.frequency.value = 523; // C5 note
      oscillator.type = 'sine';

      // Configure volume envelope
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.4);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.4);

    } catch (error) {
      console.warn('Failed to play success sound:', error);
    }
  }

  /**
   * Enable or disable audio notifications
   */
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  /**
   * Check if audio notifications are enabled
   */
  getEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Initialize audio context on user interaction (required by browser policies)
   */
  async initializeOnUserInteraction() {
    if (!this.audioContext) {
      this.initializeAudioContext();
    }
    await this.resumeAudioContext();
  }
}

export const audioNotificationService = new AudioNotificationService();
